import React, { useState, useEffect } from 'react';
import { FolderOpen, Upload, Search, Download, Pencil, Trash2, RotateCcw, FileText, File } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';
import UploadDocumentModal from '../../../components/admin/crm/UploadDocumentModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = {
  contracts: { label: 'Contracts', color: 'bg-purple-100 text-purple-800' },
  proposals: { label: 'Proposals', color: 'bg-blue-100 text-blue-800' },
  invoices: { label: 'Invoices', color: 'bg-green-100 text-green-800' },
  reports: { label: 'Reports', color: 'bg-yellow-100 text-yellow-800' },
  legal: { label: 'Legal', color: 'bg-red-100 text-red-800' },
  other: { label: 'Other', color: 'bg-gray-100 text-gray-800' }
};

const ENTITY_TYPES = {
  client: 'Client',
  lead: 'Lead',
  opportunity: 'Opportunity',
  contact: 'Contact'
};

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [pagination, setPagination] = useState({ skip: 0, limit: 20 });
  const [totalCount, setTotalCount] = useState(0);
  const { toast } = useToast();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchDocuments();
    fetchCount();
  }, [pagination, searchTerm, categoryFilter, entityTypeFilter]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
      const categoryParam = categoryFilter ? `&category=${categoryFilter}` : '';
      const entityTypeParam = entityTypeFilter ? `&entity_type=${entityTypeFilter}` : '';
      
      const response = await fetch(
        `${API_URL}/api/crm/documents?skip=${pagination.skip}&limit=${pagination.limit}${searchParam}${categoryParam}${entityTypeParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch documents');

      const data = await response.json();
      setDocuments(data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast({
        title: 'Error',
        description: 'Failed to load documents',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCount = async () => {
    try {
      const categoryParam = categoryFilter ? `?category=${categoryFilter}` : '';
      const entityTypeParam = entityTypeFilter ? (categoryParam ? '&' : '?') + `entity_type=${entityTypeFilter}` : '';
      
      const response = await fetch(
        `${API_URL}/api/crm/documents/count${categoryParam}${entityTypeParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch count');

      const data = await response.json();
      setTotalCount(data.count);
    } catch (error) {
      console.error('Error fetching count:', error);
    }
  };

  const handleDownload = async (documentId, filename) => {
    try {
      const response = await fetch(`${API_URL}/api/crm/documents/${documentId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to download document');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Success',
        description: 'Document downloaded'
      });
    } catch (error) {
      console.error('Error downloading document:', error);
      toast({
        title: 'Error',
        description: 'Failed to download document',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await fetch(`${API_URL}/api/crm/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete document');

      toast({
        title: 'Success',
        description: 'Document deleted successfully'
      });
      fetchDocuments();
      fetchCount();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete document',
        variant: 'destructive'
      });
    }
  };

  const handleRestore = async (documentId) => {
    try {
      const response = await fetch(`${API_URL}/api/crm/documents/${documentId}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to restore document');

      toast({
        title: 'Success',
        description: 'Document restored successfully'
      });
      fetchDocuments();
    } catch (error) {
      console.error('Error restoring document:', error);
      toast({
        title: 'Error',
        description: 'Failed to restore document',
        variant: 'destructive'
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleNextPage = () => {
    if (pagination.skip + pagination.limit < totalCount) {
      setPagination({ ...pagination, skip: pagination.skip + pagination.limit });
    }
  };

  const handlePrevPage = () => {
    if (pagination.skip > 0) {
      setPagination({ ...pagination, skip: Math.max(0, pagination.skip - pagination.limit) });
    }
  };

  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;
  const totalPages = Math.ceil(totalCount / pagination.limit);

  return (
    <div>
      <BackButton />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-[#00B4D8] bg-opacity-10 rounded-lg">
              <FolderOpen className="h-6 w-6 text-[#00B4D8]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#2B2D42]">Documents</h2>
              <p className="text-sm text-[#6C757D]">Manage documents for clients, leads, and opportunities</p>
            </div>
          </div>
          <Button
            onClick={() => setIsUploadModalOpen(true)}
            className="bg-[#00B4D8] hover:bg-[#0096B8]"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Document
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#6C757D]" />
              <input
                type="text"
                placeholder="Search by filename..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPagination({ ...pagination, skip: 0 });
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPagination({ ...pagination, skip: 0 });
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
            >
              <option value="">All Categories</option>
              {Object.entries(CATEGORIES).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>

            {/* Entity Type Filter */}
            <select
              value={entityTypeFilter}
              onChange={(e) => {
                setEntityTypeFilter(e.target.value);
                setPagination({ ...pagination, skip: 0 });
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
            >
              <option value="">All Entity Types</option>
              {Object.entries(ENTITY_TYPES).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Documents List */}
      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">Loading documents...</p>
          </CardContent>
        </Card>
      ) : documents.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">
              {searchTerm || categoryFilter || entityTypeFilter ? 'No documents found matching your filters.' : 'No documents yet. Upload your first document!'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {documents.map((doc) => (
            <Card key={doc.id} className={doc.is_deleted ? 'opacity-50' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="p-3 bg-gray-100 rounded-lg">
                      {doc.mime_type.includes('pdf') ? (
                        <FileText className="h-6 w-6 text-red-600" />
                      ) : (
                        <File className="h-6 w-6 text-[#6C757D]" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="text-lg font-semibold text-[#2B2D42]">
                          {doc.filename}
                        </h3>
                        <span className={`px-2 py-1 text-xs rounded-full ${CATEGORIES[doc.category]?.color || CATEGORIES.other.color}`}>
                          {CATEGORIES[doc.category]?.label || 'Other'}
                        </span>
                        {doc.is_deleted && (
                          <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                            Deleted
                          </span>
                        )}
                      </div>
                      {doc.description && (
                        <p className="text-sm text-[#6C757D] mb-2">{doc.description}</p>
                      )}
                      <div className="flex flex-wrap gap-3 text-sm text-[#6C757D]">
                        <div>
                          <span className="font-medium">Entity:</span> {ENTITY_TYPES[doc.entity_type]}
                        </div>
                        <div>
                          <span className="font-medium">Size:</span> {formatFileSize(doc.file_size)}
                        </div>
                        <div>
                          <span className="font-medium">Uploaded:</span> {formatDate(doc.created_at)}
                        </div>
                        <div>
                          <span className="font-medium">By:</span> {doc.uploaded_by}
                        </div>
                      </div>
                      {doc.tags && doc.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {doc.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-0.5 text-xs bg-[#E0F2FE] text-[#00B4D8] rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    {doc.is_deleted ? (
                      <Button
                        onClick={() => handleRestore(doc.id)}
                        variant="outline"
                        size="sm"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Restore
                      </Button>
                    ) : (
                      <>
                        <Button
                          onClick={() => handleDownload(doc.id, doc.filename)}
                          variant="outline"
                          size="sm"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(doc.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalCount > pagination.limit && (
        <div className="mt-6 flex items-center justify-center space-x-4">
          <Button
            onClick={handlePrevPage}
            disabled={pagination.skip === 0}
            variant="outline"
          >
            Previous
          </Button>
          <span className="text-sm text-[#6C757D]">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            onClick={handleNextPage}
            disabled={pagination.skip + pagination.limit >= totalCount}
            variant="outline"
          >
            Next
          </Button>
        </div>
      )}

      {/* Upload Modal */}
      <UploadDocumentModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSuccess={() => {
          setIsUploadModalOpen(false);
          fetchDocuments();
          fetchCount();
        }}
      />
    </div>
  );
};

export default Documents;
